import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { createPortal } from "react-dom";
import { Search, Loader2, ScanFace, LogIn, LogOut, MapPin, CalendarDays, ListChecks, Pencil, Trash2, X, Plus, FileDown, FileSpreadsheet } from "lucide-react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { api, apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";

const ddmmyyyy = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
  if (isNaN(d)) return "—";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
};

let LOGO_CACHE = null;
async function getLogoDataUrl() {
  if (LOGO_CACHE) return LOGO_CACHE;
  try {
    const res = await fetch("/logo-lg.webp");
    const blob = await res.blob();
    const img = await createImageBitmap(blob);
    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;
    canvas.getContext("2d").drawImage(img, 0, 0);
    LOGO_CACHE = canvas.toDataURL("image/png");
  } catch {
    LOGO_CACHE = null;
  }
  return LOGO_CACHE;
}
const STATUS_LABEL = { present: "Present", late: "Late", off_day: "Off Day", absent: "Absent", review: "Review", pending: "Pending" };

function Thumb({ path, alt }) {
  const [ok, setOk] = useState(true);
  if (!path || !ok)
    return <div className="h-10 w-10 rounded-lg bg-slate-100 grid place-items-center text-slate-300"><ScanFace className="h-4 w-4" /></div>;
  return <img src={fileUrl(path)} alt={alt} onError={() => setOk(false)} className="h-10 w-10 rounded-lg object-cover border border-slate-200" />;
}

function fmt(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso + "T00:00:00").toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
}
function fmtTime(ts) {
  if (!ts) return "—";
  return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

const STATUS_OPTIONS = [
  ["all", "All statuses"],
  ["present", "Present"],
  ["late", "Late"],
  ["off_day", "Off Day"],
  ["absent", "Absent"],
  ["review", "Review"],
  ["pending", "Pending"],
];

export default function Attendance() {
  const qc = useQueryClient();
  const { me } = useAuth();
  const canEdit = me?.can_edit;
  const [tab, setTab] = useState("report");
  const [addOpen, setAddOpen] = useState(false);

  const auditMut = useMutation({
    mutationFn: () => api.post("/admin/audit/run"),
    onSuccess: (r) => {
      toast.success(`AI audit run — ${r.selected} record(s) audited`);
      qc.invalidateQueries({ queryKey: ["attendance"] });
      qc.invalidateQueries({ queryKey: ["reviews"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <div className="space-y-5 animate-fade-up" data-testid="attendance-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1" data-testid="attendance-tabs">
          <button
            onClick={() => setTab("report")}
            data-testid="tab-report"
            className={`inline-flex items-center gap-2 h-9 px-4 rounded-md text-sm font-semibold transition ${tab === "report" ? "bg-indigo-600 text-white" : "text-slate-500 hover:text-slate-800"}`}
          >
            <CalendarDays className="h-4 w-4" /> Daily Report
          </button>
          <button
            onClick={() => setTab("log")}
            data-testid="tab-log"
            className={`inline-flex items-center gap-2 h-9 px-4 rounded-md text-sm font-semibold transition ${tab === "log" ? "bg-indigo-600 text-white" : "text-slate-500 hover:text-slate-800"}`}
          >
            <ListChecks className="h-4 w-4" /> Event Log
          </button>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <button onClick={() => setAddOpen(true)} data-testid="add-attendance-btn"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition disabled:opacity-60 shadow-sm shadow-indigo-600/20">
              <Plus className="h-4 w-4" /> Add Attendance
            </button>
          )}
          <button onClick={() => auditMut.mutate()} disabled={auditMut.isPending} data-testid="run-audit-btn"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700 transition disabled:opacity-60 shadow-sm shadow-sky-600/20">
            {auditMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanFace className="h-4 w-4" />} Run AI Audit
          </button>
        </div>
      </div>

      {tab === "report" ? <DailyReport /> : <EventLog />}
      <AddAttendanceModal open={addOpen} onClose={() => setAddOpen(false)} />
    </div>
  );
}

function AddAttendanceModal({ open, onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ user_id: "", type: "in", status: "present", ts: "" });
  const [err, setErr] = useState("");

  const { data: employees = [] } = useQuery({
    queryKey: ["employees-min"],
    queryFn: () => api.get("/admin/employees"),
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      const d = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      setForm({ user_id: "", type: "in", status: "present", ts: local });
      setErr("");
    }
  }, [open]);

  const mut = useMutation({
    mutationFn: (body) => api.post("/admin/attendance", body),
    onSuccess: () => {
      toast.success("Attendance record added");
      qc.invalidateQueries({ queryKey: ["attendance"] });
      qc.invalidateQueries({ queryKey: ["attendance-report"] });
      onClose();
    },
    onError: (e) => setErr(apiError(e)),
  });

  if (!open) return null;

  const submit = (ev) => {
    ev.preventDefault();
    setErr("");
    if (!form.user_id) { setErr("Please select an employee"); return; }
    const body = { user_id: form.user_id, type: form.type, ts: new Date(form.ts).toISOString() };
    if (form.type === "in") body.status = form.status;
    mut.mutate(body);
  };

  return createPortal(
    <div className="fixed inset-0 z-[100]">
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="min-h-full flex items-center justify-center p-4">
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden my-4" data-testid="add-attendance-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="font-head text-lg font-bold text-slate-900">Add Attendance Record</h3>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4" data-testid="add-attendance-form">
              {err && <p className="text-sm text-rose-600 bg-rose-50 rounded-lg px-3 py-2">{err}</p>}
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Employee</label>
                <select data-testid="add-att-employee" value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })}
                  className="w-full h-11 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100">
                  <option value="">Select employee…</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>{emp.name} ({emp.staff_id})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Type</label>
                <select data-testid="add-att-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className="w-full h-11 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100">
                  <option value="in">Clock In</option>
                  <option value="out">Clock Out</option>
                </select>
              </div>
              {form.type === "in" && (
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Status</label>
                  <select data-testid="add-att-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full h-11 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100">
                    <option value="present">Present</option>
                    <option value="absent">Absent</option>
                    <option value="review">Review</option>
                    <option value="off_day">Off Day</option>
                  </select>
                  <p className="mt-1 text-xs text-slate-400">"Late" is auto-derived from the clock-in time vs. the shift start.</p>
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Date &amp; Clock Time</label>
                <input data-testid="add-att-time" type="datetime-local" required value={form.ts} onChange={(e) => setForm({ ...form, ts: e.target.value })}
                  className="w-full h-11 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={mut.isPending} data-testid="add-att-save"
                  className="h-10 px-4 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 inline-flex items-center gap-2">
                  {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Add Record
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function DailyReport() {
  const qc = useQueryClient();
  const { me } = useAuth();
  const canEdit = me?.can_edit;
  const canDelete = me?.can_delete;
  const showActions = canEdit || canDelete;
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [employee, setEmployee] = useState("all");
  const [site, setSite] = useState("all");
  const [range, setRange] = useState(30);
  const [editRow, setEditRow] = useState(null);
  const [exporting, setExporting] = useState(false);
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["attendance-report", range],
    queryFn: () => api.get(`/admin/attendance/report?days=${range}`),
  });

  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/admin/attendance/${id}`),
    onSuccess: () => { toast.success("Attendance record deleted"); qc.invalidateQueries({ queryKey: ["attendance-report"] }); qc.invalidateQueries({ queryKey: ["attendance"] }); },
    onError: (e) => toast.error(apiError(e)),
  });

  const employeeOptions = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => { if (!m.has(r.user_id)) m.set(r.user_id, `${r.name} (${r.staff_id})`); });
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);
  const siteOptions = useMemo(() => {
    return [...new Set(rows.map((r) => r.site_name).filter(Boolean))].sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter((r) => {
      if (status !== "all" && r.status !== status) return false;
      if (employee !== "all" && r.user_id !== employee) return false;
      if (site !== "all" && (r.site_name || "") !== site) return false;
      if (q && !(r.name?.toLowerCase().includes(q) || r.staff_id?.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [rows, search, status, employee, site]);

  const counts = useMemo(() => {
    const c = { present: 0, late: 0, off_day: 0, absent: 0, review: 0, pending: 0 };
    rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  const exportCols = ["Date", "Weekday", "Employee", "Staff ID", "Clock-in", "Site", "Status"];
  const exportMatrix = () =>
    filtered.map((r) => [
      ddmmyyyy(r.day), r.weekday || "—", r.name || "—", r.staff_id || "—",
      r.clock_in_time ? fmtTime(r.clock_in_time) : "—", r.site_name || "—", STATUS_LABEL[r.status] || r.status,
    ]);
  const exportSubtitle = () => {
    const parts = [`Last ${range} days`];
    if (employee !== "all") parts.push(employeeOptions.find((e) => e[0] === employee)?.[1] || "");
    if (site !== "all") parts.push(site);
    if (status !== "all") parts.push(STATUS_LABEL[status] || status);
    return parts.filter(Boolean).join("  •  ");
  };

  const exportPDF = async () => {
    setExporting(true);
    try {
      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const logo = await getLogoDataUrl();
      if (logo) doc.addImage(logo, "PNG", 40, 28, 46, 46);
      doc.setFont("helvetica", "bold").setFontSize(18).setTextColor(15, 23, 42);
      doc.text("Smart-In", logo ? 98 : 40, 46);
      doc.setFont("helvetica", "normal").setFontSize(11).setTextColor(100, 116, 139);
      doc.text("Attendance Logs", logo ? 98 : 40, 63);
      doc.setFontSize(9).setTextColor(148, 163, 184);
      doc.text(exportSubtitle(), logo ? 98 : 40, 77);
      doc.setDrawColor(226, 232, 240);
      doc.line(40, 88, doc.internal.pageSize.getWidth() - 40, 88);
      autoTable(doc, {
        startY: 98,
        head: [exportCols],
        body: exportMatrix(),
        styles: { fontSize: 9, cellPadding: 5 },
        headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        margin: { left: 40, right: 40 },
      });
      const pw = doc.internal.pageSize.getWidth();
      const ph = doc.internal.pageSize.getHeight();
      const today = new Date().toISOString().slice(0, 10);
      doc.setFontSize(8).setTextColor(148, 163, 184);
      doc.text(`Generated ${ddmmyyyy(today)} • Smart-In`, 40, ph - 20);
      doc.text(`${filtered.length} record(s)`, pw - 40, ph - 20, { align: "right" });
      doc.save(`Attendance_Logs_${today}.pdf`);
    } catch {
      toast.error("PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  const exportExcel = () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const aoa = [["Smart-In — Attendance Logs"], [exportSubtitle()], [], exportCols, ...exportMatrix()];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws["!cols"] = exportCols.map(() => ({ wch: 18 }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Attendance Logs");
      XLSX.writeFile(wb, `Attendance_Logs_${today}.xlsx`);
    } catch {
      toast.error("Excel export failed");
    }
  };

  const emptyExport = filtered.length === 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="report-summary">
        {[
          ["Present", counts.present, "text-emerald-600"],
          ["Late", counts.late, "text-orange-600"],
          ["Off Day", counts.off_day, "text-indigo-600"],
          ["Absent", counts.absent, "text-rose-600"],
          ["Review", counts.review, "text-amber-600"],
          ["Pending", counts.pending, "text-slate-500"],
        ].map(([label, val, tint]) => (
          <div key={label} className="bg-white border border-slate-200 rounded-xl shadow-sm p-3">
            <p className={`text-2xl font-head font-black ${tint}`}>{val}</p>
            <p className="text-xs text-slate-500">{label}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input data-testid="report-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search employee…"
                className="w-full h-10 rounded-lg border border-slate-200 pl-10 pr-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100" />
            </div>
            <select data-testid="report-employee-filter" value={employee} onChange={(e) => setEmployee(e.target.value)}
              className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-indigo-500 max-w-[220px]">
              <option value="all">All employees</option>
              {employeeOptions.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>
            <select data-testid="report-site-filter" value={site} onChange={(e) => setSite(e.target.value)}
              className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-indigo-500 max-w-[200px]">
              <option value="all">All sites</option>
              {siteOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <select data-testid="report-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
              className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-indigo-500">
              {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <select data-testid="report-range-filter" value={range} onChange={(e) => setRange(Number(e.target.value))}
              className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-indigo-500">
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
            </select>
            <div className="sm:ml-auto flex items-center gap-2">
              <button onClick={exportPDF} disabled={exporting || emptyExport} data-testid="export-pdf-btn"
                className="inline-flex items-center gap-2 h-10 px-3.5 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition">
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4 text-rose-500" />} PDF
              </button>
              <button onClick={exportExcel} disabled={emptyExport} data-testid="export-excel-btn"
                className="inline-flex items-center gap-2 h-10 px-3.5 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition">
                <FileSpreadsheet className="h-4 w-4 text-emerald-600" /> Excel
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase text-slate-400 border-b border-slate-100 bg-slate-50/50">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Weekday</th>
                <th className="px-4 py-3">Employee</th>
                <th className="px-4 py-3">Clock-in</th>
                <th className="px-4 py-3">Site</th>
                <th className="px-4 py-3">Status</th>
                {showActions && <th className="px-4 py-3 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50" data-testid="report-table-body">
              {isLoading ? (
                <tr><td colSpan={showActions ? 7 : 6} className="px-4 py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={showActions ? 7 : 6} className="px-4 py-10 text-center text-slate-400">No records</td></tr>
              ) : (
                filtered.map((r) => (
                  <tr key={`${r.user_id}-${r.day}`} className="hover:bg-slate-50/60 transition" data-testid={`report-row-${r.staff_id}-${r.day}`}>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(r.day)}</td>
                    <td className="px-4 py-3 text-slate-500">{r.weekday}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-800">{r.name}</p>
                      <p className="font-mono text-xs text-slate-400">{r.staff_id}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap font-mono text-xs">{r.clock_in_time ? fmtTime(r.clock_in_time) : "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{r.site_name || "—"}</td>
                    <td className="px-4 py-3"><StatusBadge status={r.status} testid={`report-status-${r.staff_id}-${r.day}`} /></td>
                    {showActions && (
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {canEdit && (
                            <button onClick={() => setEditRow(r)}
                              data-testid={`report-edit-${r.staff_id}-${r.day}`} title={r.event_id ? "Edit record" : "Set / override status"}
                              className="h-8 w-8 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-indigo-600 transition">
                              <Pencil className="h-4 w-4" />
                            </button>
                          )}
                          {canDelete && r.event_id && (
                            <button onClick={() => { if (window.confirm(`Delete ${r.name}'s attendance record for ${fmtDate(r.day)}?`)) delMut.mutate(r.event_id); }}
                              data-testid={`report-delete-${r.staff_id}-${r.day}`} title="Delete record"
                              className="h-8 w-8 grid place-items-center rounded-lg text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <OverrideModal row={editRow} onClose={() => setEditRow(null)} />
    </div>
  );
}

function OverrideModal({ row, onClose }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState("present");
  const [time, setTime] = useState("09:00");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (row) {
      const allowed = ["present", "absent", "review", "off_day"];
      const init = row.event_status && allowed.includes(row.event_status)
        ? row.event_status
        : allowed.includes(row.status) ? row.status : "present";
      setStatus(init);
      const pad = (n) => String(n).padStart(2, "0");
      const t = row.clock_in_time ? new Date(row.clock_in_time) : null;
      setTime(t ? `${pad(t.getHours())}:${pad(t.getMinutes())}` : "09:00");
      setErr("");
    }
  }, [row]);

  const mut = useMutation({
    mutationFn: (body) => (row.event_id ? api.put(`/admin/attendance/${row.event_id}`, body) : api.post("/admin/attendance", body)),
    onSuccess: () => {
      toast.success(row.event_id ? "Attendance record updated" : "Attendance record created");
      qc.invalidateQueries({ queryKey: ["attendance-report"] });
      qc.invalidateQueries({ queryKey: ["attendance"] });
      onClose();
    },
    onError: (e) => setErr(apiError(e)),
  });

  if (!row) return null;

  const submit = (ev) => {
    ev.preventDefault();
    setErr("");
    const ts = new Date(`${row.day}T${time}:00`).toISOString();
    if (row.event_id) mut.mutate({ type: "in", status, ts });
    else mut.mutate({ user_id: row.user_id, type: "in", status, ts });
  };

  return createPortal(
    <div className="fixed inset-0 z-[100]">
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="min-h-full flex items-center justify-center p-4">
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden my-4" data-testid="override-attendance-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="font-head text-lg font-bold text-slate-900">
                {row.event_id ? "Edit" : "Set"} Attendance — {row.name}
              </h3>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4" data-testid="override-attendance-form">
              {err && <p className="text-sm text-rose-600 bg-rose-50 rounded-lg px-3 py-2" data-testid="override-error">{err}</p>}
              <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 text-sm text-slate-600">
                <span className="font-semibold">{fmtDate(row.day)}</span> · {row.weekday}
                {!row.event_id && <span className="ml-2 text-xs text-amber-600">(no record yet — this creates one)</span>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Status</label>
                <select data-testid="override-status" value={status} onChange={(e) => setStatus(e.target.value)}
                  className="w-full h-11 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100">
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                  <option value="review">Review</option>
                  <option value="off_day">Off Day</option>
                </select>
                <p className="mt-1 text-xs text-slate-400">"Late" is auto-derived from the clock-in time vs. the shift start.</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Clock-in Time</label>
                <input data-testid="override-time" type="time" required value={time} onChange={(e) => setTime(e.target.value)}
                  className="w-full h-11 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={mut.isPending} data-testid="override-save"
                  className="h-10 px-4 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 inline-flex items-center gap-2">
                  {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} {row.event_id ? "Save Changes" : "Create Record"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function EventLog() {
  const qc = useQueryClient();
  const { me } = useAuth();
  const canEdit = me?.can_edit;
  const canDelete = me?.can_delete;
  const showActions = canEdit || canDelete;
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [editEvent, setEditEvent] = useState(null);
  const { data: events = [], isLoading } = useQuery({ queryKey: ["attendance"], queryFn: () => api.get("/admin/attendance?limit=1000") });

  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/admin/attendance/${id}`),
    onSuccess: () => { toast.success("Attendance record deleted"); qc.invalidateQueries({ queryKey: ["attendance"] }); qc.invalidateQueries({ queryKey: ["attendance-report"] }); },
    onError: (e) => toast.error(apiError(e)),
  });

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return events.filter((e) => {
      const st = e.report_status || e.status;
      if (type !== "all" && e.type !== type) return false;
      if (status !== "all" && (st || "none") !== status) return false;
      if (q && !(e.name?.toLowerCase().includes(q) || e.staff_id?.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [events, search, status, type]);

  const colSpan = showActions ? 8 : 7;

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input data-testid="attendance-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search employee…"
            className="w-full h-10 rounded-lg border border-slate-200 pl-10 pr-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100" />
        </div>
        <select data-testid="attendance-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
          className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-indigo-500">
          <option value="all">All statuses</option>
          <option value="present">Present</option>
          <option value="late">Late</option>
          <option value="review">Review</option>
          <option value="absent">Absent</option>
        </select>
        <select data-testid="attendance-type-filter" value={type} onChange={(e) => setType(e.target.value)}
          className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-indigo-500">
          <option value="all">In &amp; Out</option>
          <option value="in">Clock In</option>
          <option value="out">Clock Out</option>
        </select>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-semibold uppercase text-slate-400 border-b border-slate-100 bg-slate-50/50">
              <th className="px-4 py-3">Selfie</th>
              <th className="px-4 py-3">Employee</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Site</th>
              <th className="px-4 py-3">Time</th>
              <th className="px-4 py-3">AI Confidence</th>
              <th className="px-4 py-3">Status</th>
              {showActions && <th className="px-4 py-3 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50" data-testid="attendance-table-body">
            {isLoading ? (
              <tr><td colSpan={colSpan} className="px-4 py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={colSpan} className="px-4 py-10 text-center text-slate-400">No attendance records</td></tr>
            ) : (
              filtered.map((e) => {
                const conf = e.audit_result?.confidence;
                return (
                  <tr key={e.id} className="hover:bg-slate-50/60 transition" data-testid={`attendance-row-${e.id}`}>
                    <td className="px-4 py-3"><Thumb path={e.selfie_path} alt={e.name} /></td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-800">{e.name}</p>
                      <p className="font-mono text-xs text-slate-400">{e.staff_id}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold ${e.type === "in" ? "text-emerald-600" : "text-slate-500"}`}>
                        {e.type === "in" ? <LogIn className="h-3.5 w-3.5" /> : <LogOut className="h-3.5 w-3.5" />}
                        {e.type === "in" ? "In" : "Out"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {e.site_name ? <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-slate-400" />{e.site_name}</span> : "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmt(e.ts)}</td>
                    <td className="px-4 py-3">
                      {conf != null ? (
                        <div className="flex items-center gap-2 w-28">
                          <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                            <div className={`h-full rounded-full ${conf >= 98 ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${Math.min(conf, 100)}%` }} />
                          </div>
                          <span className="text-xs font-mono text-slate-600">{Math.round(conf)}%</span>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={e.type === "in" ? (e.report_status || e.status) : null} /></td>
                    {showActions && (
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {canEdit && (
                            <button onClick={() => setEditEvent(e)} data-testid={`attendance-edit-${e.id}`} title="Edit record"
                              className="h-8 w-8 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-indigo-600 transition">
                              <Pencil className="h-4 w-4" />
                            </button>
                          )}
                          {canDelete && (
                            <button onClick={() => { if (window.confirm(`Delete this ${e.type === "in" ? "clock-in" : "clock-out"} record for ${e.name}?`)) delMut.mutate(e.id); }}
                              data-testid={`attendance-delete-${e.id}`} title="Delete record"
                              className="h-8 w-8 grid place-items-center rounded-lg text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <EditAttendanceModal event={editEvent} onClose={() => setEditEvent(null)} />
    </div>
  );
}

function EditAttendanceModal({ event, onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ type: "in", status: "present", ts: "" });
  const [err, setErr] = useState("");

  useEffect(() => {
    if (event) {
      const d = event.ts ? new Date(event.ts) : new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      setForm({ type: event.type || "in", status: event.status || "present", ts: local });
    }
  }, [event]);

  const mut = useMutation({
    mutationFn: (body) => api.put(`/admin/attendance/${event.id}`, body),
    onSuccess: () => {
      toast.success("Attendance record updated");
      qc.invalidateQueries({ queryKey: ["attendance"] });
      qc.invalidateQueries({ queryKey: ["attendance-report"] });
      onClose();
    },
    onError: (e) => setErr(apiError(e)),
  });

  if (!event) return null;

  const submit = (ev) => {
    ev.preventDefault();
    setErr("");
    const body = { type: form.type, ts: new Date(form.ts).toISOString() };
    if (form.type === "in") body.status = form.status;
    mut.mutate(body);
  };

  return createPortal(
    <div className="fixed inset-0 z-[100]">
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="min-h-full flex items-center justify-center p-4">
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden my-4" data-testid="edit-attendance-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="font-head text-lg font-bold text-slate-900">Edit Attendance — {event.name}</h3>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4" data-testid="edit-attendance-form">
              {err && <p className="text-sm text-rose-600 bg-rose-50 rounded-lg px-3 py-2">{err}</p>}
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Type</label>
                <select data-testid="edit-att-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className="w-full h-11 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100">
                  <option value="in">Clock In</option>
                  <option value="out">Clock Out</option>
                </select>
              </div>
              {form.type === "in" && (
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Status</label>
                  <select data-testid="edit-att-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full h-11 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100">
                    <option value="present">Present</option>
                    <option value="absent">Absent</option>
                    <option value="review">Review</option>
                    <option value="off_day">Off Day</option>
                  </select>
                  <p className="mt-1 text-xs text-slate-400">"Late" is auto-derived from the clock-in time vs. the shift start.</p>
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Clock Time</label>
                <input data-testid="edit-att-time" type="datetime-local" required value={form.ts} onChange={(e) => setForm({ ...form, ts: e.target.value })}
                  className="w-full h-11 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={mut.isPending} data-testid="edit-att-save"
                  className="h-10 px-4 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 inline-flex items-center gap-2">
                  {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
